import axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { ENDPOINTS } from './endpoints';
import { useAuthStore } from '../stores/useAuthStore';
import { parseHostname } from '../env/hostname';
import { safeStorage } from '../utils/safeStorage';

const etagCache = new Map<string, { etag: string; data: unknown }>();

export function getEtagForUrl(url: string): string | undefined {
  return etagCache.get(url)?.etag;
}

// Interceptor below sets `config.baseURL` per-request; `apiClient.defaults.baseURL` is never assigned. Callers needing the URL ahead of a request must use this.
export function getApiBaseUrl(): string {
  if (import.meta.env.VITE_LOCAL_API_URL) {
    return import.meta.env.VITE_LOCAL_API_URL;
  }
  if (import.meta.env.DEV) {
    const tenant = import.meta.env.VITE_LOCAL_TENANT;
    const env = import.meta.env.VITE_LOCAL_ENV;
    return env
      ? `https://${tenant}.${env}.api.sera.tech`
      : `https://${tenant}.api.sera.tech`;
  }
  const { tenant, envSegment, hasEnvSegment } = parseHostname(
    window.location.hostname,
  );
  if (hasEnvSegment) {
    return `https://${tenant}.${envSegment}.api.sera.tech`;
  }
  return `https://${tenant}.api.sera.tech`;
}

export const apiClient = axios.create();

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    config.baseURL = getApiBaseUrl();

    if (
      import.meta.env.VITE_LOCAL_API_URL &&
      import.meta.env.VITE_LOCAL_TENANT
    ) {
      config.headers['Tenant'] = import.meta.env.VITE_LOCAL_TENANT;
    }

    const authToken = safeStorage.getItem('authToken');
    const authUserStr = safeStorage.getItem('authUser');

    if (authToken && authUserStr) {
      try {
        const authUser = JSON.parse(authUserStr) as {
          role: string;
          email: string;
          id: number | string;
        };
        config.headers['Authorization'] =
          `Token token=${authToken}; role=${authUser.role}; email=${authUser.email}; id=${authUser.id}`;
      } catch {
        // corrupted authUser — skip auth header
      }
    }

    const cachedEtag = etagCache.get(config.url ?? '');
    if (cachedEtag) {
      config.headers['If-None-Match'] = cachedEtag.etag;
    }

    return config;
  },
);

type ResponseError = {
  config?: { url?: string };
  response?: { status: number; data?: unknown; [key: string]: unknown };
};

function resolveFromCache(error: ResponseError) {
  const cached = etagCache.get(error.config?.url ?? '');
  if (cached)
    return {
      ...error.response,
      status: 200,
      data: cached.data,
      _servedFromCache: true,
    };
  return null;
}

// quick-260528-fgq: V1 endpoints scoped to the `appointment_viewer` Devise
// group (currently /v1/appointments/:id show + /v1/appointments/:id/share)
// return 401 with body {"response":"Unable to authenticate"} when the user IS
// authenticated but isn't allowed to view that specific appointment — see
// api: app/controllers/v1/appointments_controller.rb:42,105 (the `unless
// @appointment` branches that run AFTER Devise has already authenticated).
// Devise's own auth-failure path returns a different body ({"error":"Invalid
// session..."}), so the response field is a reliable signal that the 401 is a
// resource-permission denial rather than a session expiry. Without this guard
// a customer who opens an appointment URL they don't own gets bounced back to
// /login with the same redirect param, looping forever.
function isResourcePermissionDenialAs401(error: ResponseError): boolean {
  const data = error.response?.data;
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { response?: unknown }).response === 'Unable to authenticate'
  );
}

function handleUnauthorized(error: ResponseError) {
  if (isResourcePermissionDenialAs401(error)) {
    // Caller's catch handler renders the appropriate "not found" UI; the
    // session stays valid so the next navigation works without re-login.
    return;
  }
  const url = error.config?.url ?? '';
  // Endpoints that legitimately 401 without indicating a session-expired
  // condition for the *current* session: sign_in/sign_out are bracketing the
  // session lifecycle; v2/customers reset_password is a self-service flow;
  // forgot_password is unauthenticated; findByEmail is the boot-time probe
  // used by restoreSession() to validate a stored token — its 401 is handled
  // locally by the store (clear storage) and must NOT bounce the user from
  // public routes (e.g. /schedule-appointment).
  const isAuthEndpoint =
    url.includes(ENDPOINTS.v1.users.signIn) ||
    url.includes(ENDPOINTS.v1.users.signOut) ||
    url.includes('/v2/customers/') ||
    url.includes(ENDPOINTS.v1.users.forgotPassword) ||
    url.includes(ENDPOINTS.v1.users.findByEmail) ||
    // Magic-link token validation (GET /v1/tokens/:token/auth) lives on the
    // public /appointment-details route (meta.requiresAuth:false). A 401 here
    // means the shared link is expired/invalid — MagicLinkAppointmentView must
    // render its own "no longer active" state, NOT bounce the anonymous
    // recipient to /login via auth:session-expired (R1 / PODZ-1560 AC #6).
    /\/v1\/tokens\/[^/]+\/auth/.test(url);
  if (!isAuthEndpoint) {
    useAuthStore().handleUnauthorized();
  }
}

apiClient.interceptors.response.use(
  (response) => {
    const etag = response.headers['etag'];
    if (etag && response.config?.url) {
      etagCache.set(response.config.url, { etag, data: response.data });
    }
    return response;
  },
  async (error: ResponseError) => {
    if (error.response?.status === 304) {
      const resolved = resolveFromCache(error);
      if (resolved) return Promise.resolve(resolved);
    }

    if (error.response?.status === 401) {
      handleUnauthorized(error);
    }
    return Promise.reject(error);
  },
);
